import { useMemo, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import {
  GalleryLightbox,
  GallerySharedStyles,
  type GalleryImage,
} from './_galleryShared';

/**
 * Filterable photo gallery — renders a single grid (with its own lightbox)
 * whose contents switch between "All", "Apartment", and "Community" via a
 * row of filter tabs. Two collection bindings drive the categories: photos
 * whose `title` is exactly "Apartment" and photos whose `title` is exactly
 * "Community". The "All" tab concatenates both, apartment shots first.
 */
type GalleryFilter = 'all' | 'apartment' | 'community';

const FILTER_TABS: { key: GalleryFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'apartment', label: 'Apartment' },
  { key: 'community', label: 'Community' },
];

export function GalleryApartments() {
  const apartmentImages = useCollectionBinding<GalleryImage>('galleryapartments.images', {
    collection: 'photo_gallery',
    filter: { title: 'Apartment' },
  });

  const communityImages = useCollectionBinding<GalleryImage>('galleryapartments.communityImages', {
    collection: 'photo_gallery',
    filter: { title: 'Community' },
  });

  const [filter, setFilter] = useState<GalleryFilter>('all');
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const allImages = useMemo(
    () => [...apartmentImages, ...communityImages],
    [apartmentImages, communityImages],
  );

  const displayedImages =
    filter === 'apartment'
      ? apartmentImages
      : filter === 'community'
        ? communityImages
        : allImages;

  // Switching tabs re-indexes the grid, so drop any open lightbox selection.
  const handleFilter = (next: GalleryFilter) => {
    setFilter(next);
    setSelectedIndex(null);
  };

  if (allImages.length === 0) {
    return (
      <section className="bg-white">
        <div className="gallery-constrained py-12 text-center text-[var(--vibe-color-text-muted)]">
          <p className="font-body text-lg">Gallery images will be displayed here.</p>
        </div>
        <GallerySharedStyles />
      </section>
    );
  }

  return (
    <section className="bg-white">
      <div className="gallery-constrained pt-8 md:pt-12 pb-12">
        <div className="gallery-tabs" role="tablist" aria-label="Filter gallery photos">
          {FILTER_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={filter === tab.key}
              className={`gallery-tab${filter === tab.key ? ' is-active' : ''}`}
              onClick={() => handleFilter(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {displayedImages.length > 0 ? (
          /* Full-bleed responsive grid on every breakpoint — every photo
             renders as an identical square tile, edge-to-edge with no gaps. */
          <div className="gallery-grid">
            {displayedImages.map((img, index) => (
              <div
                key={img.image || index}
                className="gallery-grid__item gallery-item group"
                onClick={() => setSelectedIndex(index)}
              >
                <VibesitesImage
                  url={img.image}
                  alt={img.alt_text || img.title || ''}
                  fill
                  className="transition-transform duration-500 group-hover:scale-110"
                  sizes="(min-width: 768px) 33vw, 50vw"
                />
              </div>
            ))}
          </div>
        ) : (
          <p className="gallery-tabs__empty font-body">
            No photos in this category yet.
          </p>
        )}
      </div>

      <GalleryLightbox
        images={displayedImages}
        selectedIndex={selectedIndex}
        onClose={() => setSelectedIndex(null)}
        onChange={setSelectedIndex}
      />

      <GallerySharedStyles />

      <style>{`
        .gallery-tabs {
          display: flex;
          justify-content: center;
          flex-wrap: wrap;
          gap: 0.75rem;
          margin-bottom: 1.75rem;
        }

        .gallery-tab {
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          padding: 0.5rem 1.35rem;
          border: 1px solid var(--vibe-color-primary);
          border-radius: 0;
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary);
          cursor: pointer;
          transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
        }
        .gallery-tab:hover {
          background: color-mix(in srgb, var(--vibe-color-primary) 85%, #000);
        }
        .gallery-tab.is-active {
          background: var(--vibe-color-secondary);
          border-color: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-secondary);
        }
        .gallery-tab.is-active:hover {
          background: var(--vibe-color-secondary);
        }

        /* Mobile: stack the filter tabs vertically, each full-width */
        @media (max-width: 639px) {
          .gallery-tabs {
            flex-direction: column;
            align-items: stretch;
            gap: 0.75rem;
            padding: 0 1rem;
          }
          .gallery-tab {
            width: 100%;
            padding: 1rem 1.35rem;
          }
        }

        .gallery-tabs__empty {
          text-align: center;
          color: var(--vibe-color-text-muted);
          font-size: 1.05rem;
          padding: 2.5rem 0;
        }

        /* Full-bleed photo grid: the images run edge-to-edge across the whole
           viewport with no gaps between them. The grid lives inside the
           width-constrained container, so break it out to 100vw and center it
           on the viewport regardless of the parent's width. */
        .gallery-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 0;
          width: 100vw;
          position: relative;
          left: 50%;
          right: 50%;
          margin-left: -50vw;
          margin-right: -50vw;
        }
        @media (min-width: 768px) {
          .gallery-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        .gallery-grid__item {
          position: relative;
          aspect-ratio: 1 / 1;
          overflow: hidden;
          cursor: pointer;
          /* Grid items default to min-height:auto, which lets a tall image's
             intrinsic height stretch its row past the 1:1 square. Pin the
             minimums to 0 so the aspect-ratio always wins and every tile is
             the same size. */
          min-width: 0;
          min-height: 0;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'GalleryApartments',
  displayName: 'Apartment Gallery',
  tokens: {},
  collectionBindings: {
    'galleryapartments.images': {
      label: 'Apartment Photos',
      collection: 'photo_gallery',
      filter: { title: 'Apartment' },
      description:
        'Pulled from the property photo gallery. Photos whose title is exactly "Apartment" appear under the Apartment (and All) filter.',
    },
    'galleryapartments.communityImages': {
      label: 'Community Photos',
      collection: 'photo_gallery',
      filter: { title: 'Community' },
      description:
        'Pulled from the property photo gallery. Photos whose title is exactly "Community" appear under the Community (and All) filter.',
    },
  },
};

(GalleryApartments as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default GalleryApartments;
